"use client"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Shield, Users, Wrench, Target, Phone, Mail } from "lucide-react"
import { Link, usePathname, useRouter } from "@/i18n/navigation"
import Image from "next/image"
import { useTranslations } from "next-intl"

export default function Footer() {
  const t = useTranslations("Footer")
  const pathname = usePathname()
  const router = useRouter()

  const changeLang = (locale: string) => {
    router.push(`/${pathname}`, { locale });
  };
  //const router = useRouter()
  return (
    <footer className="bg-white border-t">
      {/* Section 1 - Quality Icons */}
      <div className="py-12 md:py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                <Shield className="w-12 h-12 md:w-16 md:h-16 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t("qualityAuditTitle")}</h3>
              <p className="text-sm text-gray-600">{t("qualityAuditDescription")}</p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                <Users className="w-12 h-12 md:w-16 md:h-16 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t("expertTeamTitle")}</h3>
              <p className="text-sm text-gray-600">{t("expertTeamDescription")}</p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                <Wrench className="w-12 h-12 md:w-16 md:h-16 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t("jointDevelopmentTitle")}</h3>
              <p className="text-sm text-gray-600">{t("jointDevelopmentDescription")}</p>
            </div>

            <div className="text-center">
              <div className="w-24 h-24 md:w-32 md:h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center">
                <Target className="w-12 h-12 md:w-16 md:h-16 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">{t("adaptedPerformanceTitle")}</h3>
              <p className="text-sm text-gray-600">{t("adaptedPerformanceDescription")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 2 - Newsletter (Eye-catching) */}
      <div className="py-12 md:py-16 bg-primary text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center text-center lg:text-left">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold mb-4">{t("newsletterTitle")}</h2>
                <p className="text-base md:text-lg opacity-90">
                  {t("newsletterDescription")}
                </p>
              </div>

              <div>
                <form className="flex flex-col sm:flex-row gap-3 md:gap-4">
                  <Input
                    type="email"
                    placeholder={t("emailPlaceholder")}
                    className="flex-1 bg-white text-black h-12"
                  />
                  <Button
                    variant="secondary"
                    type="submit"
                    className="bg-white text-primary hover:bg-gray-100 h-12 px-6"
                  >
                    {t("subscribeButton")}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3 - Company Info */}
      <div className="py-8 md:py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Column 1 - Company Info */}
            <div className="text-center md:text-left">
              <Image
                src="/logo.svg?height=60&width=180&text=COMPANY+LOGO"
                alt="Company Logo"
                width={180}
                height={60}
                className="mb-4 mx-auto md:mx-0 h-[50px] w-[150px] md:h-[60px] md:w-[180px]"
              />
              <p className="text-sm text-gray-600 mb-2">
                <span className="font-semibold">{t("companyName")}</span>
                <br />
                {t("companyAddress")}
              </p>
              
              <div className="space-y-2 mb-4">
                <h4 className="font-semibold">{t("questions")}</h4>
                <div className="flex items-center justify-center md:justify-start space-x-2 text-sm">
                  <Phone className="w-4 h-4" />
                  <span>+7 (701) 962-02-73</span>
                </div>
                <div className="flex items-center justify-center md:justify-start space-x-2 text-sm">
                  <Mail className="w-4 h-4" />
                  <span>eastwesttrade1@gmail.com</span>
                </div>
              </div>
            </div>

            {/* Column 2 - Languages */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold mb-4">{t("languagesTitle")}</h4>
              <div className="space-y-2">
                <button
                  type="button"
                  className="block text-sm hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  onClick={() => changeLang("en")}
                >
                  English
                </button>
                <button
                  type="button"
                  className="block text-sm hover:text-primary transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  onClick={() => changeLang("ru")}
                >
                  Русский
                </button>
              </div>
            </div>

            {/* Column 3 - Guides */}
            <div className="text-center md:text-left">
              <h4 className="font-semibold mb-4">{t("guidesTitle")}</h4>
              <div className="space-y-2 mb-4">
                <Link href="#" className="block text-sm hover:text-primary transition-colors">
                  {t("sizeGuide")}
                </Link>
              </div>

              <h5 className="font-medium mb-2 text-sm">{t("guideFiles")}</h5>
              <div className="space-y-1">
                <Link href="#" className="block text-xs hover:text-primary transition-colors">
                  {t("userGuidePdf")}
                </Link>
                <Link href="#" className="block text-xs hover:text-primary transition-colors">
                  {t("termsFrBeneluxPdf")}
                </Link>
                <Link href="#" className="block text-xs hover:text-primary transition-colors">
                  {t("termsExportPdf")}
                </Link>
                <Link href="#" className="block text-xs hover:text-primary transition-colors">
                  {t("legalNotices")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Copyright */}
      <div className="py-4 bg-gray-100 border-t">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-gray-600">{t("copyright")}</p>
        </div>
      </div>
    </footer>
  )
}